<template>
  <sc-flex :is-column="true">
    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="closePreviewDialog()"
    >
      <v-card>
        <PreviewDocument
          :document-preview-data="previewData"
          :preview-rotation="previewRotation"
          preview-type="pdf"
        />
      </v-card>
    </v-dialog>

    <UploadK1PackageDialog
      :opened.sync="uploadK1PackageDialogOpened"
      :uploading="uploadingPackages"
      @upload-packages="uploadPackages"
    />

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status == 'action_required'" type="info">
      <span>
        <b>Instruction:</b> Upload the K-1 packages if you have them.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="next"
          :is-disabled="saving"
          icon-right="arrow-right"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <ReadonlyAlert
      v-else
      :tax-return-status="taxReturn.status"
      :loading="saving"
      @next="next"
    />

    <v-layout column class="page-container">
      <v-layout row justify-end class="mt-1 auto-shrink">
        <v-btn
          color="primary"
          class="text-none"
          @click="showUploadK1PackageDialog"
          :disabled="!editable"
        >
          <v-icon class="material-icons-outlined" left>
            upload
          </v-icon>
          Upload K-1 Packages
        </v-btn>
      </v-layout>

      <v-form ref="form" lazy-validation>
        <v-data-table
          :headers="headers"
          :items="taxReturn.k1_packages"
          class="partners-table auto-shrink"
          :pagination.sync="pagination"
        >
          <template v-slot:items="props">
            <td>
              <v-text-field
                class="default-input"
                solo
                v-model="props.item.partner_name"
                placeholder="Enter partner name"
                :rules="[rules.required]"
                hide-details
                required
                :disabled="!editable"
              />
            </td>
            <td class="text-xs-left">
              <v-text-field
                class="default-input"
                solo
                v-model="props.item.partner_email"
                placeholder="Enter partner email"
                :rules="[rules.required, rules.validEmail]"
                hide-details
                required
                :disabled="!editable"
              />
            </td>
            <td class="text-xs-left">
              <v-btn
                outline
                small
                color="primary"
                class="text-none mx-0"
                @click="previewDocument(props.item.document_path)"
              >
                <v-icon
                  small
                  class="material-icons-outlined mr-1 primary--text"
                  left
                >
                  file_open
                </v-icon>
                {{ props.item.custom_package_name }}
              </v-btn>
            </td>

            <td class="text-xs-right">
              <v-btn icon flat>
                <v-menu
                  transition="slide-y-transition"
                  bottom
                  left
                  nudge-bottom="30"
                  class="my-1"
                >
                  <v-icon slot="activator">
                    more_vert
                  </v-icon>

                  <v-list>
                    <v-list-tile @click="remove(props.item)">
                      <v-list-tile-title> Remove </v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-form>
    </v-layout>
  </sc-flex>
</template>

<script>
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import axios from 'axios';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import UploadK1PackageDialog from '@/components/returns/UploadK1PackageDialog';
import PreviewDocument from '@/components/PreviewDocument';

export default {
  components: {
    ReadonlyAlert,
    UploadK1PackageDialog,
    PreviewDocument,
  },

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      headers: [
        {
          text: 'Partner name',
          align: 'left',
          value: 'partner_name',
          sortable: false,
        },
        {
          text: 'Partner email',
          value: 'partner_email',
          align: 'left',
          sortable: false,
        },
        {
          text: 'K-1 package',
          value: 'custom_package_name',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Action',
          align: 'right',
          sortable: false,
        },
      ],

      pagination: {
        rowsPerPage: -1,
      },

      uploadingPackages: false,
      uploadK1PackageDialogOpened: false,
      saving: false,

      rules: {
        required: value => !!value || 'Required.',
        validEmail: value => /.+@.+\..+/.test(value) || 'Email must be valid.',
      },

      // preview package
      previewData: null,
      previewRotation: 0,
      previewLoaded: false,
      previewDialogOpened: false,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    editable() {
      return this.taxReturn.status === 'action_required';
    },
  },

  methods: {
    next() {
      if (!this.validateInputs()) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'Please fill out all the required fields and make sure they are valid.',
        });
      } else {
        this.$emit('next');
      }
    },

    showUploadK1PackageDialog() {
      this.uploadK1PackageDialogOpened = true;
    },

    async uploadPackages(packages) {
      try {
        this.uploadingPackages = true;

        for (const k1Package of packages) {
          const {
            data: { id, upload_link },
          } = await API.taxReturn.getUploadLinkForK1Package({
            companyId: this.currentCompany.id,
          });

          await API.integration.uploadToDropbox(upload_link, k1Package);

          const response = await API.taxReturn.processK1Package({
            fileId: id,
            taxReturnId: this.taxReturn.id,
            documentName: k1Package.name,
            companyId: this.currentCompany.id,
          });

          await this.addPackage(
            response.data.document_name,
            response.data.document_location,
          );
        }

        this.uploadK1PackageDialogOpened = false;
        await this.save();
      } catch (error) {
        console.log(error);

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the documents.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.uploadingPackages = false;
      }
    },

    async addPackage(packageName, documentPath) {
      const newPackage = {
        custom_package_name: packageName,
        document_path: documentPath,
        partner_name: '',
        partner_email: '',
      };

      if (!Array.isArray(this.taxReturn.k1_packages)) {
        this.taxReturn.k1_packages = [];
      }

      this.taxReturn.k1_packages.push(newPackage);
    },

    async remove(k1Package) {
      this.taxReturn.k1_packages = this.taxReturn.k1_packages.filter(
        p => p !== k1Package,
      );

      await this.save();
    },

    validateInputs() {
      return this.$refs.form.validate();
    },

    async save() {
      if (!this.editable) {
        return;
      }

      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        ...this.taxReturn,
        k1_packages: this.taxReturn.k1_packages,
      });

      return response.data;
    },

    closePreviewDialog() {
      this.previewData = '';
      this.previewRotation = 0;
      this.previewDialogOpened = false;
    },

    async previewDocument(documentPath) {
      this.previewLoaded = false;
      this.previewType = null;
      this.previewData = null;
      this.previewRotation = 0;

      this.previewDialogOpened = true;

      try {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: documentPath,
        });

        this.previewData = response.data.document.data.link;
      } catch (e) {
        console.log(e);

        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
        });
      } finally {
        this.previewLoaded = true;
      }
    },
  },

  watch: {},

  async beforeDestroy() {
    await this.save();
  },
};
</script>

<style lang="postcss" scoped>
.page-container {
  flex: auto;
  width: 100%;
  height: 100%;
  min-height: calc(100dvh - 120px);
  max-height: calc(100dvh - 120px);
  overflow: auto;
}

.scrollable-container {
}

.partners-table {
  margin: 0px 16px 16px;
}

.default-input {
}
</style>
